import { useCallback, useEffect, useRef, useState } from 'react';

import { useGetCelebrationQuery } from '@entities/celebration';
import { useGetMeQuery } from '@entities/user';

const DAY_MS = 24 * 60 * 60 * 1_000;
const NEW_USER_WINDOW_MS = DAY_MS;
const PREVIEW_KEY = 'yeneshop:celebration:preview';
const playedThisSession = new Set<string>();
let previewQueuedThisSession = false;

/** Queue one admin-requested preview for the next visit to the shop home page. */
export function queueStorefrontCelebrationPreview(): void {
  // Keep an in-memory fallback for private webviews where localStorage throws.
  previewQueuedThisSession = true;
  try {
    localStorage.setItem(PREVIEW_KEY, '1');
  } catch {
    // The route change keeps this module alive, so the fallback is sufficient.
  }
}

function consumeStorefrontCelebrationPreview(): boolean {
  let queued = previewQueuedThisSession;
  try {
    queued ||= localStorage.getItem(PREVIEW_KEY) === '1';
    localStorage.removeItem(PREVIEW_KEY);
  } catch {
    // Fall back to the module-level flag.
  }
  previewQueuedThisSession = false;
  return queued;
}

function readTime(key: string): number {
  try {
    const value = Number(localStorage.getItem(key));
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function writeTime(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // The in-memory guard below still prevents repeated bursts this session.
  }
}

/** Decides once per StorePage mount whether this device is due a celebration. */
export function useStorefrontCelebration() {
  const { data: settings } = useGetCelebrationQuery();
  const { data: user } = useGetMeQuery();
  const evaluated = useRef(false);
  const [runId, setRunId] = useState<number | null>(null);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    if (!user || evaluated.current) return;

    // An explicit admin preview is consumed only here, on the Store page. It
    // bypasses scheduling and session guards without updating either of them.
    if (consumeStorefrontCelebrationPreview()) {
      evaluated.current = true;
      setIsPreview(true);
      setRunId(Date.now());
      return;
    }

    if (!settings) return;
    evaluated.current = true;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (playedThisSession.has(user.id)) return;

    const now = Date.now();
    const lastPlayedKey = `yeneshop:celebration:last:${user.id}`;
    const welcomedKey = `yeneshop:celebration:welcomed:${user.id}`;
    const isNewUser = now - Date.parse(user.createdAt) <= NEW_USER_WINDOW_MS;
    const welcomeDue = settings.newUsersEnabled && isNewUser && readTime(welcomedKey) === 0;
    const recurringDue =
      settings.recurringEnabled &&
      now - readTime(lastPlayedKey) >= settings.intervalDays * DAY_MS;

    if (!welcomeDue && !recurringDue) return;

    // Claim it before animation begins. Navigating away halfway through must
    // not turn the celebration into a replay loop.
    playedThisSession.add(user.id);
    writeTime(lastPlayedKey, now);
    if (welcomeDue) writeTime(welcomedKey, now);
    setRunId(now);
  }, [settings, user]);

  const finish = useCallback(() => {
    setRunId(null);
    setIsPreview(false);
  }, []);
  return { runId, finish, isPreview };
}
